import { parseICS, CalendarResponse, VEvent, VCalendar } from 'node-ical'

import conf from 'conf'
import icsDB from 'libs/DB/icsDB'
import { customFetchWithToken } from 'services/jwt'
import { refineEvent } from '../RefineEvent'
import type { CalendarEventObject } from '../CalendarSync'
import { _makeCalendarUrl, _makeEventUrl } from './calendarHelpers'
import type { ICSSubscription, FetchResult } from './types'
import { REFRESH_INTERVALS } from './types'

/**
 * ICS URL이 유효한 HTTP(S) URL인지 검증한다.
 */
export const _isValidIcsUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * URL 경로에서 캘린더 이름을 추출한다.
 * 예: https://calendars.icloud.com/holidays/kr_ko.ics → "kr_ko"
 */
const _extractNameFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname
    const filename = pathname.split('/').filter(Boolean).pop() ?? ''
    const name = filename.replace(/\.ics$/i, '').trim()
    return name.length > 0 ? name : null
  } catch {
    return null
  }
}

/**
 * ICS 원문 텍스트를 파싱하여 이벤트를 생성한다.
 */
export const _parseICSText = (icsText: string, subscription: ICSSubscription): FetchResult => {
  const calendarUrl = _makeCalendarUrl(subscription.id)
  const vCalendar = parseICS(icsText) as CalendarResponse
  const eventDict: Record<string, CalendarEventObject> = {}

  // VCALENDAR 컴포넌트에서 메타데이터 추출
  const vcal = Object.values(vCalendar).find((c) => c.type === 'VCALENDAR') as VCalendar | undefined
  const calendarName = vcal?.['WR-CALNAME'] || _extractNameFromUrl(subscription.url) || null
  const timezone = vcal?.['WR-TIMEZONE'] || null

  Object.entries(vCalendar).forEach(([_key, component]) => {
    if (component.type !== 'VEVENT') return

    let event = component as VEvent
    event = refineEvent(event)
    const eventUid = event.uid || _key
    const eventUrl = _makeEventUrl(subscription.id, eventUid)

    eventDict[eventUrl] = {
      url: eventUrl,
      etag: '',
      color: subscription.color,
      event,
      folderId: calendarUrl,
      dirty: true,
    }
  })

  return { events: eventDict, calendarName, timezone }
}

/**
 * ICS URL에서 데이터를 다운로드하고 파싱하여 이벤트를 생성한다.
 * 다운로드한 ICS 원문은 icsDB에 캐시한다.
 */
export const _fetchAndParseICS = async (
  subscription: ICSSubscription,
): Promise<FetchResult | null> => {
  if (!_isValidIcsUrl(subscription.url)) {
    console.error('ICS URL validation failed: invalid URL scheme for %s', subscription.url)
    return null
  }

  try {
    const response = await customFetchWithToken(`${conf.SERVER.restURL}/calendar/download-ics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: subscription.url }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error(
        'ICS proxy failed: %s %s',
        response.status,
        errorData.message || response.statusText,
      )
      return null
    }

    const icsText = await response.text()

    // ICS 원문을 캐시에 저장 (후속 _shouldRefresh가 정확한 fetchedAt을 읽도록 await)
    await icsDB.putData(subscription.id, { icsText, fetchedAt: Date.now() }).catch(() => {})

    return _parseICSText(icsText, subscription)
  } catch (error) {
    console.error('ICS fetch/parse error for %s: %o', subscription.url, error)
    return null
  }
}

/**
 * 캐시된 fetchedAt과 refreshInterval을 비교하여 다운로드 필요 여부를 판단한다.
 */
export const _shouldRefresh = async (subscription: ICSSubscription): Promise<boolean> => {
  try {
    const cached = await icsDB.getData(subscription.id)
    if (!cached?.data?.fetchedAt) return true

    if (subscription.refreshInterval === 'no') return false

    const interval = REFRESH_INTERVALS[subscription.refreshInterval]
    return Date.now() - cached.data.fetchedAt >= interval
  } catch {
    return true
  }
}

/**
 * icsDB 캐시에서 ICS 원문을 로드하고 파싱한다.
 */
export const _loadCachedICS = async (
  subscription: ICSSubscription,
): Promise<FetchResult | null> => {
  try {
    await icsDB.getDB()
    const cached = await icsDB.getData(subscription.id)
    if (!cached?.data?.icsText) return null
    return _parseICSText(cached.data.icsText, subscription)
  } catch {
    return null
  }
}
